// Image wrapper that shows a shimmering Skeleton until the image decodes,
// then cross-fades the actual image in. Keeps catalog tiles from flashing
// blank while bundled assets are still being decoded by the browser.
import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface ImageWithSkeletonProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Tailwind class for object-fit. Defaults to "cover". */
  objectFit?: "cover" | "contain";
  /** Extra class for the wrapping <div>. */
  wrapperClassName?: string;
  /** Extra padding on the image — useful with object-contain product shots. */
  imageClassName?: string;
  /**
   * When true, marks the image as critical (above the fold): `loading="eager"`,
   * `fetchpriority="high"`, and synchronous decode hint. Use sparingly — only
   * for the first ~6 thumbnails in a viewport.
   */
  eager?: boolean;
}

export const ImageWithSkeleton = ({
  src,
  alt,
  objectFit = "cover",
  wrapperClassName,
  imageClassName,
  className,
  loading,
  eager = false,
  ...rest
}: ImageWithSkeletonProps) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  return (
    <div className={cn("relative h-full w-full", wrapperClassName)}>
      {!loaded && !errored && (
        <Skeleton className="absolute inset-0 h-full w-full rounded-[inherit]" />
      )}
      <img
        src={src}
        alt={alt}
        loading={loading ?? (eager ? "eager" : "lazy")}
        decoding={eager ? "sync" : "async"}
        // @ts-expect-error fetchpriority is valid HTML but missing from React's types in this version
        fetchpriority={eager ? "high" : undefined}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        className={cn(
          "h-full w-full transition-opacity duration-300",
          objectFit === "cover" ? "object-cover" : "object-contain",
          loaded ? "opacity-100" : "opacity-0",
          imageClassName,
          className,
        )}
        {...rest}
      />
    </div>
  );
};
